import * as React from 'react';
import { FOCUSABLE_SELECTOR } from '../utils/focusable';
import { useLatestRef } from './use-latest-ref';

export interface UseModalFocusOptions {
    open: boolean;
    dialogRef: React.RefObject<HTMLElement>;
    /** Called after closing when the element that opened the modal is gone, e.g. a row the modal deleted. */
    onOpenerRemoved?: () => void;
}

const tabbablesIn = (dialog: HTMLElement): HTMLElement[] =>
    Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => element.getClientRects().length > 0,
    );

/**
 * Keyboard focus for an open modal (WCAG 2.1.2 / 2.4.3), filling two gaps in flowbite's focus manager:
 * - its Shift+Tab guard can hand focus to the page behind instead of wrapping, so Tab wraps here;
 * - on close it can return focus to <body>, or to an earlier modal's opener, so the element that opened this
 *   modal is restored here, or `onOpenerRemoved` runs when that element no longer exists.
 */
export const useModalFocus = ({ open, dialogRef, onOpenerRemoved }: UseModalFocusOptions): void => {
    const onOpenerRemovedRef = useLatestRef(onOpenerRemoved);

    // A layout effect: flowbite moves focus into the dialog in a microtask after this commit, so focus is
    // still on the opener here.
    React.useLayoutEffect(() => {
        if (!open) {
            return;
        }

        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

        const onKeyDown = (event: KeyboardEvent): void => {
            const dialog = dialogRef.current;
            if (event.key !== 'Tab' || !dialog || !dialog.contains(event.target as Node)) {
                return;
            }
            const tabbables = tabbablesIn(dialog);
            if (tabbables.length === 0) {
                return;
            }
            const first = tabbables[0];
            const last = tabbables[tabbables.length - 1];
            const active = document.activeElement;
            if (event.shiftKey && (active === first || active === dialog)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            // After flowbite's own return-focus attempt, which runs in a microtask on close and can pick an
            // earlier modal's opener from floating-ui's history. Leaves focus alone if it has moved into
            // another dialog, e.g. one this modal handed over to.
            setTimeout(() => {
                if (document.activeElement?.closest('[role="dialog"]')) {
                    return;
                }
                if (opener?.isConnected) {
                    opener.focus();
                } else {
                    onOpenerRemovedRef.current?.();
                }
            }, 0);
        };
    }, [open, dialogRef, onOpenerRemovedRef]);
};
